/**
 * 범용 API 클라이언트
 * 환경변수를 통한 설정 지원
 */

// 환경변수에서 API 기본 URL 가져오기
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7030';

/**
 * API 에러 클래스
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * 범용 API 클라이언트 클래스
 */
class ApiClient {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * 기본 헤더 생성
   */
  getDefaultHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    // 토큰이 있으면 Authorization 헤더 추가
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * HTTP 요청 메서드
   * @param {string} endpoint - API 엔드포인트
   * @param {object} options - 요청 옵션
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      method: 'GET',
      headers: {
        ...this.getDefaultHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      // FormData 로깅 처리
      let logBody = '';
      if (config.body) {
        if (config.body instanceof FormData) {
          logBody = '[FormData]';
          // FormData 내용 간단히 출력
          const formDataEntries = [];
          for (let pair of config.body.entries()) {
            formDataEntries.push(`${pair[0]}: ${pair[1] instanceof File ? `File(${pair[1].name})` : pair[1]}`);
          }
          logBody = `[FormData: ${formDataEntries.join(', ')}]`;
        } else {
          logBody = JSON.parse(config.body);
        }
      }
      
      const response = await fetch(url, config);
      
      // Content-Type 확인하여 JSON 파싱
      const contentType = response.headers.get('content-type');
      let data = null;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        throw new ApiError(
          data?.message || data || `HTTP error! status: ${response.status}`,
          response.status,
          data
        );
      }

      return data;
    } catch (error) {
      
    }
  }

  /**
   * GET 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {object} params - 쿼리 파라미터
   * @param {AbortSignal} signal - AbortController signal
   */
  async get(endpoint, params = {}, signal = null) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    const options = { method: 'GET' };
    if (signal) {
      options.signal = signal;
    }
    
    return this.request(url, options);
  }

  /**
 * 파일 다운로드 요청
 * @param {string} endpoint - API 엔드포인트
 * @param {object} params - 쿼리 파라미터
 * @param {AbortSignal} signal - AbortController signal
 * @param {string} filename - 저장할 파일명
 */
async download(endpoint, params = {}, signal = null, filename = "download.xlsx") {
  const queryString = new URLSearchParams(params).toString();
  const url = queryString ? `${this.baseURL}${endpoint}?${queryString}` : `${this.baseURL}${endpoint}`;

  const options = {
    method: "GET",
    headers: {
      ...this.getDefaultHeaders(),
    },
  };
  if (signal) {
    options.signal = signal;
  }

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`파일 다운로드 실패 (status: ${response.status})`);
    }

    // 파일을 blob으로 받기
    const blob = await response.blob();

    // 파일을 브라우저에서 다운로드 트리거
    const fileUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(fileUrl);

  } catch (error) {
    console.error("Download error:", error);
    throw error;
  }
}
  /**
   * POST 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {object} data - 요청 데이터
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {object} data - 요청 데이터
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE 요청
   * @param {string} endpoint - API 엔드포인트
   */
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * PATCH 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {object} data - 요청 데이터
   */
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * 파일 업로드 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {FormData} formData - 파일 데이터
   */
  async upload(endpoint, formData) {
    const headers = {};
    
    // 토큰이 있으면 Authorization 헤더 추가 (Content-Type은 자동 설정)
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return this.request(endpoint, {
      method: 'POST',
      headers,
      body: formData,
    });
  }

  /**
 * HTTP 요청 (타임아웃 지원)
 * @param {string} endpoint - API 엔드포인트
 * @param {FormData} formData - 파일 데이터
 * @param {number} timeout - 타임아웃(ms)
 */
async uploadRequestWithTimeout(endpoint, formData, timeout = 5000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const headers = {};
    
    // 토큰이 있으면 Authorization 헤더 추가 (Content-Type은 자동 설정)
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  try {
    return await this.request(endpoint, {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
    });
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new ApiError('요청이 시간 초과되었습니다.', 408, null);
    }
    throw error;
  } finally {
    clearTimeout(id);
  }
}
}

// 기본 인스턴스 생성 및 내보내기
const apiClient = new ApiClient();

export { ApiClient, apiClient };
export default apiClient;